import { useEffect, useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useAppNavigation } from '@features/navigation/hooks/useAppNavigation'
import { useOptionalRouteCatalog } from '@features/navigation/components/RouteCatalogContext'
import { SETTING_DEFINITIONS, useSettingsStore } from '@features/settings/stores/useSettingsStore'
import type { FavoriteDescription } from '../logic/favorites'
import { createRouteDescriber } from './useFavorites'

/**
 * Names addresses from their pages' metadata, as favorites are named
 * (describeFavorite), from what the app already has: each address's
 * description, in order, or null until they're worked out. The last ones
 * stay while new addresses' are on their way.
 */
export function useDescribedPaths(paths: readonly string[] | null): FavoriteDescription[] | null {
  const root = useOptionalRouteCatalog()
  const { modules, modulesPending, modulesError } = useAppNavigation()
  const queryClient = useQueryClient()
  const pathsKey = paths?.join('\n')
  // Which areas the user may open is unknown, not none, when their modules
  // didn't load: no address reads as switched off then.
  const availableAreasKey = modulesError
    ? null
    : modules.map((module) => module.pathPrefix).join('|')
  const [described, setDescribed] = useState<FavoriteDescription[] | null>(null)

  useEffect(() => {
    // Until the areas arrive, every address would look switched off.
    if (!root || pathsKey === undefined || modulesPending) return
    let cancelled = false
    const availableAreas = availableAreasKey === null ? null : new Set(availableAreasKey.split('|'))
    const describe = createRouteDescriber(root, availableAreas, queryClient)
    const list = pathsKey ? pathsKey.split('\n') : []
    void Promise.all(list.map((path) => describe(path, 'cached'))).then((descriptions) => {
      if (!cancelled) setDescribed(descriptions)
    })
    return () => {
      cancelled = true
    }
  }, [availableAreasKey, modulesPending, pathsKey, queryClient, root])

  return described
}

/**
 * The page Home opens, by its trail (Tables › Accounts), as the Home Page
 * preference reads it; null until it's worked out, or when it's gone.
 */
export function useHomePageTrail(): string[] | null {
  const homePage = useSettingsStore((state) => state.homePage)
  const path = homePage ?? SETTING_DEFINITIONS.homePage.systemDefault
  const paths = useMemo(() => (path ? [path] : null), [path])
  const description = useDescribedPaths(paths)?.[0]
  return description && description.path === path && description.status !== 'gone'
    ? description.parts
    : null
}
